import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Lightbulb, FileText, BookOpen, Brain, Shield, Zap,
  CheckCircle, XCircle, Clock, Play, AlertTriangle,
  RefreshCw, X, ChevronDown, ChevronRight
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const CAPABILITY_LABELS = {
  description_review: { label: 'Description Review', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  kb_update: { label: 'Knowledge Update', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50' },
  loi_prompt: { label: 'LOI Prompt', icon: Brain, color: 'text-amber-600', bg: 'bg-amber-50' },
  compliance: { label: 'Compliance', icon: Shield, color: 'text-green-600', bg: 'bg-green-50' },
};

const STATUS_STYLES = {
  pending_review: { label: 'Pending Review', color: 'text-amber-600', bg: 'bg-amber-100', icon: Clock },
  approved: { label: 'Approved', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
  applied: { label: 'Applied', color: 'text-blue-600', bg: 'bg-blue-100', icon: Zap },
  rejected: { label: 'Rejected', color: 'text-red-600', bg: 'bg-red-100', icon: XCircle },
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { label: status, color: 'text-gray-600', bg: 'bg-gray-100', icon: Clock };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.color}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const CapabilityBadge = ({ capability }) => {
  const c = CAPABILITY_LABELS[capability] || { label: capability, icon: Zap, color: 'text-gray-600', bg: 'bg-gray-50' };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

const AdminTradosAgentSuggestions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState(searchParams.get('status') || '');
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const params = suggestionFilter ? `?status=${suggestionFilter}` : '';
      const res = await api.get(`/admin/trados-agent/suggestions${params}`);
      setSuggestions(res.data.suggestions || []);
    } catch (err) { toast.error('Failed to load suggestions'); }
    finally { setSuggestionsLoading(false); }
  }, [suggestionFilter]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const handleFilterChange = (f) => {
    setSuggestionFilter(f);
    if (f) {
      setSearchParams({ status: f });
    } else {
      setSearchParams({});
    }
  };

  const reviewSuggestion = async (id, action) => {
    try {
      await api.put(`/admin/trados-agent/suggestions/${id}`, { action });
      toast.success(`Suggestion ${action}d`);
      fetchSuggestions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <AgentLayout>
      <div className="space-y-4">
        <div className="flex gap-2">
          {['', 'pending_review', 'approved', 'applied', 'rejected'].map(f => (
            <button key={f}
              onClick={() => handleFilterChange(f)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                suggestionFilter === f ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >{f ? (STATUS_STYLES[f]?.label || f) : 'All'}</button>
          ))}
        </div>

        <div className="space-y-3">
          {suggestionsLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-12 text-gray-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading suggestions...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-12 text-gray-400">
              <Lightbulb className="w-8 h-8 mb-2" />
              <p className="text-sm">No suggestions yet</p>
              <p className="text-xs mt-1">Run a task to generate suggestions</p>
            </div>
          ) : (
            suggestions.map(s => (
              <div key={s._id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSuggestion(expandedSuggestion === s._id ? null : s._id)}
                >
                  <div className="flex items-center gap-3">
                    {expandedSuggestion === s._id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <CapabilityBadge capability={s.capability} />
                    <StatusBadge status={s.status} />
                    <span className="text-sm text-gray-600 truncate max-w-md">{(s.content || '').slice(0, 80)}...</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(s.created_at)}</span>
                </div>
                {expandedSuggestion === s._id && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {s.original_content && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Original</p>
                        <div className="bg-red-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{s.original_content}</div>
                      </div>
                    )}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Suggestion</p>
                      <div className="bg-green-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{s.content}</div>
                    </div>
                    {s.status === 'pending_review' && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => reviewSuggestion(s._id, 'apply')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
                          <Zap className="w-3.5 h-3.5" /> Apply
                        </button>
                        <button onClick={() => reviewSuggestion(s._id, 'approve')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => reviewSuggestion(s._id, 'reject')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50">
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentSuggestions;
